import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase, recordSpeakerView, getSessionId } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import MobileLoadingSkeleton from './MobileLoadingSkeleton';
import MobileEmptyState from './MobileEmptyState';

const formatDate = (dateString) => {
  if (!dateString) return 'TBD';
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getOrgColor = (org) => {
  switch (org?.toUpperCase()) {
    case 'UPCI': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300';
    case 'ALJC': return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
    case 'AFF': return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
    case 'WPF': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300';
    default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
};

const MobileSpeakerDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [speaker, setSpeaker] = useState(null);
  const [speakerEvents, setSpeakerEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    const checkFavorite = async () => {
      if (!user || !id) return;
      try {
        const { data } = await supabase
          .from('user_favorite_speakers')
          .select('id')
          .eq('user_id', user.id)
          .eq('speaker_id', id)
          .single();
        setIsFavorite(!!data);
      } catch (e) {}
    };
    checkFavorite();
  }, [user, id]);

  const toggleFavorite = async () => {
    if (!user) {
      window.location.href = '/login';
      return;
    }
    if (isFavorite) {
      await supabase.from('user_favorite_speakers').delete().eq('user_id', user.id).eq('speaker_id', id);
      setIsFavorite(false);
    } else {
      await supabase.from('user_favorite_speakers').insert({ user_id: user.id, speaker_id: id });
      setIsFavorite(true);
    }
  };

  useEffect(() => {
    if (id) {
      const sessionId = getSessionId();
      recordSpeakerView(id, sessionId).catch(() => {});
    }
  }, [id]);

  useEffect(() => {
    const fetchSpeaker = async () => {
      try {
        const { data, error } = await supabase.from('speakers').select('*').eq('id', id).single();
        if (error) throw error;
        setSpeaker(data);
        try {
          const { data: eventsData } = await supabase
            .from('event_speakers')
            .select(`events (id, title, start_date, end_date, venues (name, city))`)
            .eq('speaker_id', id);
          if (eventsData) {
            setSpeakerEvents(eventsData.map(e => e.events).filter(Boolean));
          }
        } catch (e) {}
      } catch (error) {
        console.error('Error fetching speaker:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSpeaker();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <div style={{ height: 'env(safe-area-inset-top)' }} />
        <div className="px-5 pt-6">
          <MobileLoadingSkeleton count={1} height={200} rounded="rounded-3xl" />
          <div className="mt-4 space-y-3">
            <MobileLoadingSkeleton count={1} height={20} rounded="rounded-xl" />
            <MobileLoadingSkeleton count={1} height={20} rounded="rounded-xl" />
            <MobileLoadingSkeleton count={1} height={60} rounded="rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!speaker) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col">
        <div style={{ height: 'env(safe-area-inset-top)' }} />
        <MobileEmptyState
          icon={<svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
          title="Speaker Not Found"
          message="The speaker you're looking for doesn't exist."
          actionLabel="Browse Speakers"
          onAction={() => window.location.href = '/speakers'}
        />
      </div>
    );
  }

  const fallbackImage = `https://ui-avatars.com/api/?name=${encodeURIComponent(speaker.name || 'Speaker')}&background=6D28D9&color=fff&size=400`;
  const speakerImage = imageError ? fallbackImage : (speaker.photo_url || speaker.image_url || speaker.image || fallbackImage);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-8">
      <div style={{ height: 'env(safe-area-inset-top)' }} />

      {/* Hero with gradient + speaker image */}
      <div className="relative bg-gradient-to-br from-primary-dark to-primary px-5 pt-6 pb-20 rounded-b-3xl">
        <div className="flex items-center justify-between mb-4">
          <Link to="/speakers" className="w-10 h-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center active:scale-95 transition-transform">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <button onClick={toggleFavorite} className="w-10 h-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center active:scale-95 transition-transform">
            <svg className={`w-5 h-5 ${isFavorite ? 'text-red-400' : 'text-white'}`} fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Speaker image overlapping */}
      <div className="px-5 -mt-16 relative z-10">
        <div className="w-28 h-28 rounded-3xl overflow-hidden bg-white dark:bg-gray-800 shadow-xl ring-4 ring-white dark:ring-gray-900">
          <img
            src={speakerImage}
            alt={speaker.name}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        </div>
      </div>

      {/* Speaker info */}
      <div className="px-5 mt-4">
        <h1 className="font-display text-2xl font-bold text-gray-900 dark:text-white">{speaker.name}</h1>
        {speaker.title && (
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">{speaker.title}</p>
        )}
        {speaker.organization && (
          <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold ${getOrgColor(speaker.organization)}`}>
            {speaker.organization}
          </span>
        )}
      </div>

      {/* Bio */}
      {speaker.bio && (
        <div className="px-5 mt-6">
          <h2 className="font-display text-lg font-bold text-gray-900 dark:text-white mb-2">About</h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">{speaker.bio}</p>
        </div>
      )}

      {/* Contact / Social */}
      {(speaker.email || speaker.website || speaker.social) && (
        <div className="px-5 mt-6">
          <h2 className="font-display text-lg font-bold text-gray-900 dark:text-white mb-3">Connect</h2>
          <div className="flex gap-3">
            {speaker.email && (
              <a href={`mailto:${speaker.email}`} className="w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center active:scale-95 transition-transform">
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              </a>
            )}
            {speaker.website && (
              <a href={speaker.website} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center active:scale-95 transition-transform">
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Events */}
      {speakerEvents.length > 0 && (
        <div className="px-5 mt-6">
          <h2 className="font-display text-lg font-bold text-gray-900 dark:text-white mb-3">Upcoming Events</h2>
          <div className="space-y-3">
            {speakerEvents.map((event) => (
              <Link
                key={event.id}
                to={`/events/${event.id}`}
                className="block bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-card mobile-card-press"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm line-clamp-2">{event.title}</h3>
                    <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
                      {formatDate(event.start_date)}
                      {event.venues?.name && ` • ${event.venues.name}`}
                    </p>
                  </div>
                  <svg className="w-5 h-5 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileSpeakerDetail;
